import { create } from "zustand";
import { dateTodayApiFormat } from "../../../utils/dateResolvers";
import { createJSONStorage, persist } from "zustand/middleware";
import globalConstant from "../../../utils/constants";

export interface IFilter {
  filterObject: {
    limit: number;
    orderStatus: string;
    supplierOrderStatus: string;
    customerType: string;
    orderHandler: string;
    filterOrderNo: string;
    filterOrderNoCheckBoxValue: boolean;
    customerId: string;
    customerName: string;
    orderType: string;
    transactionType: string;
    invoiceStatus: string;
    isSupplierInvoicePresent: boolean | undefined;
    startDate: string;
    endDate: string;
    states: string;
    deliveryMode: string;
    pendingOrderItems: string;
    voidedStatus: string;
    includeTestOrders: boolean;
    carMaker: string;
  };

  setFilterObject: (newFilterObject: any) => void;
  updateOrderHandlers: (orderHandlers: string[]) => void;
}

/** Default OH list query statuses (keep in sync with SideFilterTab orderStatuses). */
export const DEFAULT_ORDER_STATUS_FILTER =
  `&orderStatus=CBO&orderStatus=CANCELLED&orderStatus=SUPPLIER_CANCELLED&orderStatus=ACK&orderStatus=ALLOCATED&orderStatus=READY_TO_DISPATCH&orderStatus=DISPATCHED&orderStatus=SHIPPED&orderStatus=BLOCKED&orderStatus=NEW&orderStatus=RETURN_REQUESTED&orderStatus=CUSTOMER_RETURNED&orderStatus=CUSTOMER_RETURN_REJECTED&orderStatus=SUPPLIER_RETURNED&orderStatus=SUPPLIER_RETURN_REJECTED&orderStatus=DELIVERED&orderStatus=ACCEPTED&orderStatus=REJECTED&orderStatus=CUSTOMER_HOLD&orderStatus=PART_CONVERSION_STARTED&orderStatus=PART_CONVERSION_COMPLETED&orderStatus=DRAFT_ESTIMATE_SHARED&orderStatus=DRAFT_ESTIMATE_RESPONDED`;

/**
 * Bump when default orderStatus gains new statuses so persisted localStorage
 * filters are migrated once for existing users.
 */
const ORDER_FILTER_PERSIST_VERSION = 5;

const ORDER_STATUSES_ADDED_IN_V1 = ["DISPATCHED", "SHIPPED"] as const;

const ORDER_STATUSES_ADDED_IN_V2 = [
  "PART_CONVERSION_STARTED",
  "PART_CONVERSION_COMPLETED",
] as const;

const ORDER_STATUSES_ADDED_IN_V3 = ["SUPPLIER_CANCELLED"] as const;

const ORDER_STATUSES_ADDED_IN_V4 = [
  "DRAFT_ESTIMATE_SHARED",
  "DRAFT_ESTIMATE_RESPONDED",
] as const;

/** v5: FINAL_ESTIMATE_REQUESTED was renamed to DRAFT_ESTIMATE_RESPONDED. */
const ORDER_STATUS_RENAMED_IN_V5 = {
  from: "FINAL_ESTIMATE_REQUESTED",
  to: "DRAFT_ESTIMATE_RESPONDED",
} as const;

const parseOrderStatuses = (orderStatusQuery: string): string[] =>
  (orderStatusQuery || "").split("&orderStatus=").filter(Boolean);

/** The default status selection as it was before v2 (without the v2+ statuses). */
const PRE_V2_DEFAULT_ORDER_STATUSES = parseOrderStatuses(
  DEFAULT_ORDER_STATUS_FILTER,
).filter(
  (status) =>
    !(ORDER_STATUSES_ADDED_IN_V2 as readonly string[]).includes(status) &&
    !(ORDER_STATUSES_ADDED_IN_V3 as readonly string[]).includes(status) &&
    !(ORDER_STATUSES_ADDED_IN_V4 as readonly string[]).includes(status),
);

const isSameStatusSelection = (a: string[], b: string[]): boolean => {
  const setA = new Set(a);
  const setB = new Set(b);
  return setA.size === setB.size && Array.from(setA).every((s) => setB.has(s));
};

/**
 * v2: add the part conversion statuses only for users still on the previous
 * default selection, so custom status selections are left untouched.
 */
export const migrateOrderStatusFilterToV2 = (orderStatusQuery: string) =>
  isSameStatusSelection(
    parseOrderStatuses(orderStatusQuery),
    PRE_V2_DEFAULT_ORDER_STATUSES,
  )
    ? ensureOrderStatusesInFilter(orderStatusQuery, ORDER_STATUSES_ADDED_IN_V2)
    : orderStatusQuery;

/**
 * v3: SUPPLIER_CANCELLED is a terminal cancel state, so add it wherever
 * CANCELLED is already selected.
 */
export const migrateOrderStatusFilterToV3 = (orderStatusQuery: string) =>
  parseOrderStatuses(orderStatusQuery).includes("CANCELLED")
    ? ensureOrderStatusesInFilter(orderStatusQuery, ORDER_STATUSES_ADDED_IN_V3)
    : orderStatusQuery;

/**
 * v4: the estimate states follow PART_CONVERSION_COMPLETED, so add them
 * wherever PART_CONVERSION_COMPLETED is already selected.
 */
export const migrateOrderStatusFilterToV4 = (orderStatusQuery: string) =>
  parseOrderStatuses(orderStatusQuery).includes("PART_CONVERSION_COMPLETED")
    ? ensureOrderStatusesInFilter(orderStatusQuery, ORDER_STATUSES_ADDED_IN_V4)
    : orderStatusQuery;

/**
 * v5: saved filters may still hold FINAL_ESTIMATE_REQUESTED, which no longer
 * exists; swap it for DRAFT_ESTIMATE_RESPONDED (once, keeping the order).
 */
export const migrateOrderStatusFilterToV5 = (orderStatusQuery: string) => {
  const { from, to } = ORDER_STATUS_RENAMED_IN_V5;
  const statuses = parseOrderStatuses(orderStatusQuery);
  if (!statuses.includes(from)) {
    return orderStatusQuery;
  }
  const renamed = statuses
    .map((status) => (status === from ? to : status))
    .filter((status, index, all) => all.indexOf(status) === index);
  return renamed.map((status) => `&orderStatus=${status}`).join("");
};

const ensureOrderStatusesInFilter = (
  orderStatusQuery: string,
  statusesToEnsure: readonly string[],
): string => {
  let result = orderStatusQuery || "";
  const missing = statusesToEnsure.filter(
    (status) => !result.includes(`orderStatus=${status}`),
  );
  if (missing.length === 0) {
    return result;
  }

  const insert = missing.map((status) => `&orderStatus=${status}`).join("");
  const afterReady = "orderStatus=READY_TO_DISPATCH";
  if (result.includes(afterReady)) {
    return result.replace(afterReady, `${afterReady}${insert}`);
  }
  if (result) {
    return `${result}${insert}`;
  }
  return insert;
};

const useFiltersStore = create<IFilter>()(
  persist(
    (set, get) => ({
      filterObject: {
        limit: 20,
        orderStatus: DEFAULT_ORDER_STATUS_FILTER,
        supplierOrderStatus: `&supplierOrderStatus=NEW&supplierOrderStatus=CBO&supplierOrderStatus=CANCELLED&supplierOrderStatus=ACK&supplierOrderStatus=PICKED&supplierOrderStatus=READY&supplierOrderStatus=IN_REVIEW&supplierOrderStatus=REVOKED&supplierOrderStatus=NONE&supplierOrderStatus=RETURN_REQUESTED&supplierOrderStatus=ACCEPTED&supplierOrderStatus=REJECTED`,
        customerType: "",
        filterOrderNoCheckBoxValue: false,
        filterOrderNo: "",
        orderHandler: "",
        customerId: "",
        customerName: "",
        orderType: "&orderType=REGULAR&orderType=RETURN&orderType=SUBSCRIPTION",
        transactionType:
          "&transactionType=Trading&transactionType=Facilitation&transactionType=NONE",
        invoiceStatus:
          "&invoiceStatus=PAID&invoiceStatus=UNPAID&invoiceStatus=RECEIVED",
        isSupplierInvoicePresent: undefined,
        startDate: dateTodayApiFormat,
        endDate: dateTodayApiFormat,
        states: "",
        deliveryMode: "&deliveryMode=DOOR_DELIVERY&deliveryMode=SELF_PICKUP",
        pendingOrderItems: "",
        voidedStatus: "",
        includeTestOrders: false,
        carMaker: "",
      },
      currentLoggedInUserId: "",
      setFilterObject: (newFilterObject: any) => {
        set({
          filterObject: newFilterObject,
        });
      },
      updateOrderHandlers: (orderHandlers: string[]) => {
        set((state) => ({
          filterObject: {
            ...state.filterObject,
            orderHandler: ["", ...orderHandlers].join("&orderHandlerId="),
          },
        }));
      },
    }),

    {
      name: globalConstant.ORDER_FILTER_SETTINGS,
      storage: createJSONStorage(() => localStorage),
      version: ORDER_FILTER_PERSIST_VERSION,
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as IFilter | undefined;
        if (!state?.filterObject) {
          return persistedState as IFilter;
        }

        let orderStatus = state.filterObject.orderStatus;

        if (version < 1) {
          orderStatus = ensureOrderStatusesInFilter(
            orderStatus,
            ORDER_STATUSES_ADDED_IN_V1,
          );
        }

        if (version < 2) {
          orderStatus = migrateOrderStatusFilterToV2(orderStatus);
        }

        if (version < 3) {
          orderStatus = migrateOrderStatusFilterToV3(orderStatus);
        }

        if (version < 4) {
          orderStatus = migrateOrderStatusFilterToV4(orderStatus);
        }

        if (version < 5) {
          orderStatus = migrateOrderStatusFilterToV5(orderStatus);
        }

        return {
          ...state,
          filterObject: {
            ...state.filterObject,
            orderStatus,
          },
        };
      },
    },
  ),
);

export default useFiltersStore;
